import React, { useRef, useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql } from '@apollo/client'
import { validateFunc } from '../../../constraints/constraints'
import { saveRestaurantTermsConfiguration } from '../../../apollo'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'
import { Box, Typography, Input, Button, Alert } from '@mui/material'

const SAVE_RESTAURANT_TERMS_CONFIGURATION = gql`
  ${saveRestaurantTermsConfiguration}
`

function RestaurantTermsConfiguration(props) {
  const { t } = props
  const formRef = useRef()
  const urlDefault = props.restaurantTermsAndConditionsUrl || ''
  const versionDefault =
    props.restaurantTermsAndConditionsVersion != null
      ? String(props.restaurantTermsAndConditionsVersion)
      : ''

  const [urlError, setUrlError] = useState(null)
  const [versionError, setVersionError] = useState(null)

  const onBlur = (setter, field, state) => {
    setter(!validateFunc({ [field]: state }, field))
  }

  const [mutate, { loading }] = useMutation(SAVE_RESTAURANT_TERMS_CONFIGURATION)

  const validateInput = () => {
    const urlValue = formRef.current['input-restaurantTermsAndConditionsUrl'].value.trim()
    const vRaw = formRef.current['input-restaurantTermsAndConditionsVersion'].value.trim()
    const vNum = parseInt(vRaw, 10)
    const versionForValidation = Number.isNaN(vNum) ? vRaw : vNum

    const urlResult = !validateFunc(
      { restaurantTermsAndConditionsUrl: urlValue },
      'restaurantTermsAndConditionsUrl'
    )
    const versionResult = !validateFunc(
      { restaurantTermsAndConditionsVersion: versionForValidation },
      'restaurantTermsAndConditionsVersion'
    )

    setUrlError(urlResult)
    setVersionError(versionResult)
    return urlResult && versionResult
  }

  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const [successMessage, setSuccessMessage] = useState('')
  const handleSuccess = message => {
    setSuccessMessage(message)
  }
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSuccessMessage('')
    }, 3000)

    return () => clearTimeout(timeoutId)
  }, [successMessage, setSuccessMessage])
  const [errorMessage, setErrorMessage] = useState('')
  const handleError = error => {
    setErrorMessage('An error occurred while saving configuration.')
    console.error('Mutation error:', error)
  }
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setErrorMessage('')
    }, 3000)

    return () => clearTimeout(timeoutId)
  }, [errorMessage, setErrorMessage])

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            {t('Restaurant app terms and conditions')}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.form}>
        <form ref={formRef}>
          <Box>
            <Typography className={classes.labelText}>
              {t('Terms URL')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-restaurantTermsAndConditionsUrl"
              name="input-restaurantTermsAndConditionsUrl"
              placeholder="https://example.com/restaurant-terms"
              defaultValue={urlDefault}
              onBlur={event => {
                onBlur(
                  setUrlError,
                  'restaurantTermsAndConditionsUrl',
                  event.target.value.trim()
                )
              }}
              disableUnderline
              className={[
                globalClasses.input,
                urlError === false
                  ? globalClasses.inputError
                  : urlError === true
                  ? globalClasses.inputSuccess
                  : ''
              ]}
            />
          </Box>
          <Box>
            <Typography className={classes.labelText}>
              {t('Terms version')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-restaurantTermsAndConditionsVersion"
              name="input-restaurantTermsAndConditionsVersion"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              placeholder="1"
              defaultValue={versionDefault}
              onBlur={event => {
                const vRaw = event.target.value.trim()
                const vNum = parseInt(vRaw, 10)
                const val = Number.isNaN(vNum) ? vRaw : vNum
                onBlur(
                  setVersionError,
                  'restaurantTermsAndConditionsVersion',
                  val
                )
              }}
              disableUnderline
              className={[
                globalClasses.input,
                versionError === false
                  ? globalClasses.inputError
                  : versionError === true
                  ? globalClasses.inputSuccess
                  : ''
              ]}
            />
          </Box>
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={e => {
                e.preventDefault()
                if (validateInput() && !loading) {
                  const vRaw = formRef.current['input-restaurantTermsAndConditionsVersion'].value.trim()
                  const vNum = parseInt(vRaw, 10)
                  mutate({
                    variables: {
                      configurationInput: {
                        restaurantTermsAndConditionsUrl: formRef.current[
                          'input-restaurantTermsAndConditionsUrl'
                        ].value.trim(),
                        restaurantTermsAndConditionsVersion: vNum
                      }
                    },
                    onCompleted: () => {
                      handleSuccess('Configuration saved successfully!')
                    },
                    onError: err => {
                      handleError(err)
                    }
                  })
                }
              }}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(RestaurantTermsConfiguration)
